import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { PauseCircle, Snowflake, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Home-dashboard card (admins/supervisors): paused jobs sitting untouched and
// seasonal-hold jobs waiting in the archive. Hidden when there is nothing to do.
export const JobsHousekeepingCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/job-housekeeping`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (alive) { setData(r.data); setDays(String(r.data.stale_job_days)); } })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [token]);

  if (!data || (data.stale_count === 0 && data.seasonal_count === 0)) return null;

  const saveDays = async () => {
    const n = parseInt(days, 10);
    if (!n || n < 1 || n > 365) return;
    try {
      const { data: s } = await axios.put(`${API}/job-housekeeping/settings`, { stale_job_days: n, stale_reminders_enabled: data.stale_reminders_enabled !== false }, { headers: { Authorization: `Bearer ${token}` } });
      const { data: fresh } = await axios.get(`${API}/job-housekeeping`, { headers: { Authorization: `Bearer ${token}` } });
      setData({ ...fresh, ...s });
    } catch { /* admin-only; ignore */ }
  };

  return (
    <div className="ht-card p-4" data-testid="jobs-housekeeping-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-white">Jobs needing a decision</h3>
        <label className="flex items-center gap-1 text-[11px] text-slate-500">stale after
          <input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} onBlur={saveDays} className="w-12 px-1 py-0.5 rounded bg-[#0F1923] border border-[#2D3748] text-slate-200 text-xs text-center" data-testid="housekeeping-stale-days" /> days
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => navigate('/jobs?tab=paused&filter=stale')} className="text-left rounded-lg p-3 hover:bg-[#0F1923] transition-colors" style={{ border: '1px solid #2D3748' }} data-testid="housekeeping-stale">
          <div className="flex items-center gap-2 text-amber-300"><PauseCircle className="w-4 h-4" /><span className="text-xl font-semibold">{data.stale_count}</span></div>
          <p className="text-xs text-slate-300 mt-1">paused {data.stale_job_days}+ days — archive, cancel or resume</p>
          {data.stale_preview?.slice(0, 2).map((j) => <p key={j.id} className="text-[11px] text-slate-500 truncate">{j.job_number ? `${j.job_number} · ` : ''}{j.title} · {j.days_paused}d</p>)}
        </button>
        <button onClick={() => navigate('/jobs?tab=archive')} className="text-left rounded-lg p-3 hover:bg-[#0F1923] transition-colors" style={{ border: '1px solid #2D3748' }} data-testid="housekeeping-seasonal">
          <div className="flex items-center gap-2 text-[#1E88E5]"><Snowflake className="w-4 h-4" /><span className="text-xl font-semibold">{data.seasonal_count}</span></div>
          <p className="text-xs text-slate-300 mt-1">on seasonal hold — restore when the weather turns</p>
          {data.seasonal_preview?.slice(0, 2).map((j) => <p key={j.id} className="text-[11px] text-slate-500 truncate">{j.job_number ? `${j.job_number} · ` : ''}{j.title} · {j.days_archived}d</p>)}
        </button>
      </div>
      <Button size="sm" variant="ghost" className="mt-2 h-7 text-xs text-slate-400" onClick={() => navigate('/jobs?tab=archive')} data-testid="housekeeping-open-jobs">
        Open Jobs <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </Button>
    </div>
  );
};
